import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [status, setStatus] = useState(null); // 'success', 'error', or null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
        setTimeout(() => {
          setStatus(null);
          onClose();
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px] bg-black/95 backdrop-blur-2xl border border-white/10 text-white p-0 overflow-hidden"
        data-testid="contact-form-modal"
      >
        {/* Header */}
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-bold text-white">
            {t('modal.title')}
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3] mt-2">
            {t('modal.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-[#A3A3A3]">
              {t('modal.name')}
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="bg-[#050505] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#00F0FF]/50 focus:ring-[#00F0FF]/20"
              placeholder="John Smith"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-[#A3A3A3]">
              {t('modal.email')}
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-[#050505] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#00F0FF]/50 focus:ring-[#00F0FF]/20"
              placeholder="john@enterprise.com"
              data-testid="contact-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm text-[#A3A3A3]">
              {t('modal.company')}
            </Label>
            <Input
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              className="bg-[#050505] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#00F0FF]/50 focus:ring-[#00F0FF]/20"
              placeholder="Enterprise Corp"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm text-[#A3A3A3]">
              {t('modal.message')}
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="bg-[#050505] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#00F0FF]/50 focus:ring-[#00F0FF]/20 resize-none"
              placeholder="Tell us about your data intelligence needs..."
            />
          </div>

          {/* Status Messages */}
          <AnimatePresence>
            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-green-400 text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                {t('modal.success')}
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-[#FF003C] text-sm"
              >
                <AlertCircle className="w-4 h-4" />
                {t('modal.error')}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20 hover:border-[#00F0FF] transition-all duration-300 font-semibold"
            data-testid="contact-submit"
          >
            {isSubmitting ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5 border-2 border-[#00F0FF] border-t-transparent rounded-full"
              />
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                {t('modal.submit')}
              </span>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
